import { z } from 'zod';
/**
 * Anthropic-marketplace-compatible manifest. Served at
 * `GET /api/.well-known/marketplace.json`. Only `active` plugins emit entries;
 * deprecated plugins surface with a `deprecated` flag, yanked plugins
 * are filtered. Spec §3.2, §5.4.
 */
export const manifestLocalSourceSchema = z.string().min(1);
export const manifestUrlSourceSchema = z.object({
    source: z.literal('url'),
    url: z.string().url(),
});
export const manifestGitSubdirSourceSchema = z.object({
    source: z.literal('git-subdir'),
    url: z.string().url(),
    path: z.string().min(1),
    ref: z.string().min(1),
    sha: z.string().optional(),
});
export const manifestPluginSourceSchema = z.union([
    manifestLocalSourceSchema,
    manifestUrlSourceSchema,
    manifestGitSubdirSourceSchema,
]);
export const manifestPluginSchema = z.object({
    name: z.string().min(1),
    description: z.string(),
    version: z.string(),
    source: manifestPluginSourceSchema,
    category: z.string().min(1),
    keywords: z.array(z.string()),
    skills: z.array(z.string()),
    /** Map of dependency → semver constraint; `"*"` when unconstrained. */
    dependencies: z.record(z.string(), z.string()),
    /** Non-standard Cotality extension — surfaces deprecated plugins in UX. */
    deprecated: z.boolean().optional(),
});
export const manifestMetadataSchema = z.object({
    description: z.string().min(1),
    // Semver without a leading `v`. Mirrors the regex enforced by
    // `pluginManifestSchema.version` and the ManifestSmoke pipeline
    // assertion in `infra/.ado/pipelines/staging-gates.yml`.
    version: z
        .string()
        .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/, 'metadata.version must be valid semver (no leading v)'),
});
export const manifestOwnerSchema = z.object({
    name: z.string().min(1),
});
export const manifestSchema = z.object({
    $schema: z.string().url(),
    name: z.string().min(1),
    metadata: manifestMetadataSchema,
    owner: manifestOwnerSchema,
    plugins: z.array(manifestPluginSchema),
});
/**
 * `plugin.json` — the per-plugin manifest Claude Code loads. This is the
 * Anthropic plugin.json contract (only `name` required), locked behaviorally
 * to the vendored `contract/claude-code-plugin-manifest.json` via the
 * contract-lock spec. Cotality-only fields (`category`) live in the
 * `cotality-marketplace.json` sidecar, not here.
 *
 * Error messages here are kept in lockstep with the validator's copy
 * (`packages/marketplace-validator/src/schema.ts`); the two copies stay
 * message-locked via `manifest-parity.spec.ts`, which enforces both
 * `safeParse.success` parity AND message parity, so the two schemas cannot
 * drift on actionability.
 */
export const pluginManifestSchema = z
    .object({
    name: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-z0-9-]+$/, 'plugin name must be lowercase kebab-case'),
    version: z
        .string()
        .regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/, 'version must be valid semver'),
    description: z.string().min(1),
    author: z.union([
        z.string().min(1),
        z.object({
            name: z.string().min(1),
            email: z.string().email().optional(),
            url: z.string().url().optional(),
        }),
    ], {
        errorMap: () => ({
            message: 'author must be a string, or an object { "name": string, "email"?: string, "url"?: string } — e.g. { "name": "Jane Doe", "email": "jane@cotality.com" }',
        }),
    }),
    keywords: z.array(z.string()).optional(),
    // Path or paths to skill directories; we no longer read it (skill metadata
    // now comes from SKILL.md), mirrored for contract fidelity.
    skills: z.union([z.string(), z.array(z.string())]).optional(),
    displayName: z.string().optional(),
    homepage: z.string().url().optional(),
    repository: z.string().url().optional(),
    license: z.string().optional(),
    // Bare names resolve against the declaring plugin's own marketplace (per
    // the upstream contract description).
    dependencies: z
        .array(z.union([
        z.string(),
        z.object({ name: z.string().min(1), marketplace: z.string().optional() }).passthrough(),
    ]))
        .optional(),
})
    .passthrough();
//# sourceMappingURL=manifest.js.map